/*
 * WRISTORIAN Interface Customizations
 *
 * Portable behavioral enhancements that should remain separate
 * from the underlying Prestige theme files.
 */

(() => {
  'use strict';

 
  
  const newsletterSuccessCopy = {
    title: 'Welcome to the WRISTORIAN® Circle',
    narrative:
      'Thank you for joining us. We will write only when there is something worth sharing.',
    confirmation: 'Subscription confirmed.'
  };  
  
  const popupSelector = 'newsletter-popup.newsletter-popup';

  const accountButtonStyleId = 'wristorian-account-button-wipe';
  const accountButtonStyle = `
    :host {
      --wr-account-button-wipe-duration: 450ms;
      --wr-account-button-wipe-easing: cubic-bezier(.22, .61, .36, 1);
      --wr-account-focus-color: #ebe6da;
      --wr-account-focus-width: 2px;
      --wr-account-focus-offset: 3px;
    }

    .account__menu-region .button.outline {
      color: #d4af37 !important;
      border-color: #d4af37 !important;
      background-color: transparent !important;
      background-image: linear-gradient(#d4af37, #d4af37) !important;
      background-repeat: no-repeat !important;
      background-position: left center !important;
      background-size: 0% 100% !important;
      transition:
        background-size var(--wr-account-button-wipe-duration) var(--wr-account-button-wipe-easing),
        color var(--wr-account-button-wipe-duration) var(--wr-account-button-wipe-easing) !important;
    }

    .account__menu-region .button.outline svg {
      color: currentColor !important;
    }

    .account__menu-region .button.outline:is(:hover, :focus-visible) {
      color: #000 !important;
      background-color: transparent !important;
      background-size: 100% 100% !important;
    }

    .account__menu-region .button.outline:focus-visible {
      outline: var(--wr-account-focus-width) solid var(--wr-account-focus-color) !important;
      outline-offset: var(--wr-account-focus-offset) !important;
      box-shadow: none !important;
    }

    @media (prefers-reduced-motion: reduce) {
      .account__menu-region .button.outline {
        transition-duration: 0.01ms !important;
      }
    }
  `;

  const accountShadowObservers = new WeakMap();

  function installAccountButtonStyle(account) {
    const shadowRoot = account.shadowRoot;
    if (!shadowRoot) return false;

    if (!shadowRoot.getElementById(accountButtonStyleId)) {
      const style = document.createElement('style');
      style.id = accountButtonStyleId;
      style.textContent = accountButtonStyle;
      shadowRoot.append(style);
    }

    if (!accountShadowObservers.has(account)) {
      const observer = new MutationObserver(() => {
        installAccountButtonStyle(account);
      });

      observer.observe(shadowRoot, {
        childList: true
      });

      accountShadowObservers.set(account, observer);
    }

    return true;
  }

  function updateAccountButtonStyles(root = document) {
    const accounts = [];

    if (root instanceof Element && root.matches('shopify-account')) {
      accounts.push(root);
    }

    if (root.querySelectorAll) {
      accounts.push(...root.querySelectorAll('shopify-account'));
    }

    accounts.forEach(installAccountButtonStyle);
  }

  function initializeAccountButtonStyles() {
    updateAccountButtonStyles();

    if (window.customElements?.whenDefined) {
      window.customElements
        .whenDefined('shopify-account')
        .then(() => updateAccountButtonStyles());
    }

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof Element) {
            updateAccountButtonStyles(node);
          }
        });
      });
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  }

  /*
   * Reputon's carousel renders many duplicated and off-screen links into the
   * sequential keyboard order. The dedicated Read Collector Reviews CTA sits
   * immediately before the widget and remains the complete accessible route.
   * Keep one visible business-card link in the carousel's Tab order, preserve
   * every link for pointer and screen-reader use, and skip the redundant stops.
   */
  const reviewWidgetSelector = '.reputon-google-reviews-widget';
  const reviewFocusableSelector = 'a[href], button, [tabindex]';
  const reviewWidgetObservers = new WeakMap();

  function normalizeReviewWidgetFocus(widget) {
    const focusables = [...widget.querySelectorAll(reviewFocusableSelector)];
    const primaryLink =
      widget.querySelector(
        '.reputon-carousel-static-plate-container a.reputon-link[href]'
      ) || focusables[0];

    focusables.forEach((control) => {
      if (control === primaryLink) {
        if (control.getAttribute('tabindex') === '-1') {
          control.removeAttribute('tabindex');
        }

        control.dataset.wristorianReviewFocus = 'primary';
        return;
      }

      if (control.getAttribute('tabindex') !== '-1') {
        control.setAttribute('tabindex', '-1');
      }

      control.dataset.wristorianReviewFocus = 'excluded';
    });

    widget.dataset.wristorianKeyboardFlow = 'single-stop';
  }

  function installReviewKeyboardFlow(widget) {
    normalizeReviewWidgetFocus(widget);

    if (reviewWidgetObservers.has(widget)) return;

    const observer = new MutationObserver(() => {
      normalizeReviewWidgetFocus(widget);
    });

    observer.observe(widget, {
      childList: true,
      subtree: true
    });

    reviewWidgetObservers.set(widget, observer);
  }

  function updateReviewKeyboardFlow(root = document) {
    const widgets = [];

    if (root instanceof Element && root.matches(reviewWidgetSelector)) {
      widgets.push(root);
    }

    if (root.querySelectorAll) {
      widgets.push(...root.querySelectorAll(reviewWidgetSelector));
    }

    widgets.forEach(installReviewKeyboardFlow);
  }

  function initializeReviewKeyboardFlow() {
    updateReviewKeyboardFlow();

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof Element) {
            updateReviewKeyboardFlow(node);
          }
        });
      });
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  }

  function updateNewsletterSuccessCopy(root = document) {
    const popups = [];

    if (root instanceof Element && root.matches(popupSelector)) {
      popups.push(root);
    }

    if (root.querySelectorAll) {
      popups.push(...root.querySelectorAll(popupSelector));
    }

    let successFound = false;

    popups.forEach((popup) => {
      const successBanner = popup.querySelector('.banner--success');

      /*
       * Do nothing to the ordinary subscription popup.
       * The copy changes only when the success banner is present.
       */
      if (!successBanner) return;

      successFound = true;

      const introduction = popup.querySelector(
        '.v-stack.gap-4.text-center'
      );

      const title = introduction?.querySelector('.h4');
      const narrative = introduction?.querySelector('.prose');
      const italicNarrative = narrative?.querySelector('em');

      if (
        title &&
        title.textContent.trim() !== newsletterSuccessCopy.title
      ) {
        title.innerHTML =
          'Welcome to the WRISTORIAN<sup class="wr-registered-mark">®</sup> Circle';
      }

      if (italicNarrative) {
        if (
          italicNarrative.textContent.trim() !==
          newsletterSuccessCopy.narrative
        ) {
          italicNarrative.textContent =
            newsletterSuccessCopy.narrative;
        }
      } else if (
        narrative &&
        narrative.textContent.trim() !==
          newsletterSuccessCopy.narrative
      ) {
        narrative.textContent = newsletterSuccessCopy.narrative;
      }

      if (
        successBanner.textContent.trim() !==
        newsletterSuccessCopy.confirmation
      ) {
        successBanner.textContent =
          newsletterSuccessCopy.confirmation;
      }

      popup.dataset.wristorianSuccessCopy = 'applied';
    });

    return successFound;
  }

  function initializeNewsletterSuccessCopy() {
    /*
     * The success state will normally be present when the page loads.
     */
    if (updateNewsletterSuccessCopy()) return;

    /*
     * This also handles a success state inserted after the initial
     * page load. The observer disconnects once its work is finished.
     */
    const observer = new MutationObserver(() => {
      if (updateNewsletterSuccessCopy()) {
        observer.disconnect();
      }
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  }

  function initializeFooterNewsletterValidation(root = document) {
    const forms = [];

    if (root instanceof Element && root.matches('.wr-newsletter-form')) {
      forms.push(root);
    }

    if (root.querySelectorAll) {
      forms.push(...root.querySelectorAll('.wr-newsletter-form'));
    }

    forms.forEach((form) => {
      if (form.dataset.wristorianValidation === 'ready') return;

      const email = form.querySelector('input[type="email"][name="contact[email]"]');
      const message = form.querySelector('.wr-newsletter-validation');
      if (!email || !message) return;

      const clearMessage = () => {
        email.removeAttribute('aria-invalid');
        message.textContent = '';
        message.hidden = true;
      };

      const showMessage = (text) => {
        email.setAttribute('aria-invalid', 'true');
        message.textContent = text;
        message.hidden = false;
      };

      form.addEventListener('submit', (event) => {
        if (email.validity.valueMissing) {
          event.preventDefault();
          showMessage('Please enter your email address.');
          email.focus();
          return;
        }

        if (email.validity.typeMismatch) {
          event.preventDefault();
          showMessage('Please enter a valid email address.');
          email.focus();
          return;
        }

        clearMessage();
      });

      email.addEventListener('input', () => {
        if (!message.hidden) clearMessage();
      });

      form.dataset.wristorianValidation = 'ready';
    });
  }

  /*
   * Prestige and the currency app finish parts of the Header asynchronously.
   * Keep the visible controls in one stable keyboard sequence whenever either
   * integration rebuilds its markup. The native currency select is hidden by
   * the app, so its visible combobox receives the usable Tab stop.
   */
  const headerSelector = '.shopify-section--header';

  function normalizeHeaderKeyboardFlow() {
    const header = document.querySelector(headerSelector);
    if (!header) return;

    const menuButtons = [
      ...header.querySelectorAll(
        '.header__primary-nav > button[aria-controls="sidebar-menu"]'
      )
    ];

    const visibleMenuButton = menuButtons.find((button) => {
      const style = window.getComputedStyle(button);
      return (
        style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        button.getClientRects().length > 0
      );
    });

    menuButtons.forEach((button) => {
      button.tabIndex = button === visibleMenuButton ? 0 : -1;
    });

    const logoLink = header.querySelector('.header__logo > a[href]');
    if (logoLink) logoLink.tabIndex = 0;

    const nativeCurrency =
      header.querySelector('select.currency-switcher[name="doubly-currencies"]') ||
      document.querySelector(
        'body > .doubly-float select.currency-switcher[name="doubly-currencies"]'
      );
    if (nativeCurrency) nativeCurrency.tabIndex = -1;

    const visibleCurrency =
      header.querySelector('.doubly-nice-select.currency-switcher[role="combobox"]') ||
      document.querySelector(
        'body > .doubly-float .doubly-nice-select.currency-switcher[role="combobox"]'
      );

    if (visibleCurrency) {
      visibleCurrency.tabIndex = 0;
      visibleCurrency.dataset.wristorianKeyboardFocus = 'ready';

      const selectedCurrency = nativeCurrency?.selectedOptions?.[0];
      const visibleCurrencyValue = visibleCurrency.querySelector('.current');
      const currencyCode = selectedCurrency?.value?.trim();
      const currencySymbol =
        currencyCode === 'CHF'
          ? 'Fr'
          : selectedCurrency?.dataset.currencySymbol?.trim();

      if (
        visibleCurrencyValue &&
        currencySymbol &&
        visibleCurrencyValue.dataset.wristorianCurrencySymbol !== currencySymbol
      ) {
        visibleCurrencyValue.dataset.wristorianCurrencySymbol = currencySymbol;
      }
    }
  }

  function initializeHeaderKeyboardFlow() {
    normalizeHeaderKeyboardFlow();

    const observer = new MutationObserver(() => {
      normalizeHeaderKeyboardFlow();
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  }

  /*
   * Prestige renders active Collection filters beneath the toolbar. Move that
   * native removable-filter summary to the beginning of the Collection section
   * so it follows the merchant-authored title and byline. The same placement is
   * restored whenever Prestige rebuilds the section after a filter change.
   */
  const collectionSectionSelector = '.shopify-section--main-collection';
  let collectionActiveFacetsFrame = 0;

  function placeCollectionActiveFacets() {
    document.querySelectorAll(collectionSectionSelector).forEach((section) => {
      const stack = section.querySelector('.container > .v-stack');
      const activeFacets = section.querySelector(
        '.collection__main > .active-facets, .wr-collection-active-facets'
      );

      if (!stack || !activeFacets) return;

      const toolbar = stack.querySelector(':scope > .collection-toolbar');
      activeFacets.classList.add('wr-collection-active-facets');

      if (toolbar) {
        if (
          activeFacets.parentElement !== stack ||
          activeFacets.nextElementSibling !== toolbar
        ) {
          stack.insertBefore(activeFacets, toolbar);
        }
      } else if (activeFacets.parentElement !== stack) {
        stack.prepend(activeFacets);
      }
    });
  }

  function scheduleCollectionActiveFacets() {
    if (collectionActiveFacetsFrame) return;

    collectionActiveFacetsFrame = window.requestAnimationFrame(() => {
      collectionActiveFacetsFrame = 0;
      placeCollectionActiveFacets();
    });
  }

  function initializeCollectionActiveFacets() {
    placeCollectionActiveFacets();

    const observer = new MutationObserver(() => {
      scheduleCollectionActiveFacets();
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  }

  function initializeWristorianInterface() {
    initializeNewsletterSuccessCopy();
    initializeFooterNewsletterValidation();
    initializeAccountButtonStyles();
    initializeReviewKeyboardFlow();
    initializeHeaderKeyboardFlow();
    initializeCollectionActiveFacets();
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initializeWristorianInterface,
      { once: true }
    );
  } else {
    initializeWristorianInterface();
  }

  /*
   * Supports section reloading inside Shopify's Theme Editor.
   */
  document.addEventListener('shopify:section:load', (event) => {
    updateNewsletterSuccessCopy(event.target);
    initializeFooterNewsletterValidation(event.target);
    updateAccountButtonStyles(event.target);
    updateReviewKeyboardFlow(event.target);
    normalizeHeaderKeyboardFlow();
    scheduleCollectionActiveFacets();
  });
})();
